import React from 'react';

import { formatMoney, useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Card, Heading, Separator, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary } from '@betterment/shared-components';

import { TaxSavingsDocument } from './graphql/taxSavings.generated';

import { DateRangeOption } from '../../../../graphql/schema';
import { TaxLossHarvestingPerformanceModal } from '../../../../Shared/components-v2/tax-loss-harvesting/TaxLossHarvestingPerformanceModal';
import { TaxLossHarvestingTooltip } from '../../../../Shared/components-v2/tax-loss-harvesting/TaxLossHarvestingTooltip';

interface TaxSavingsCardViewProps {
  dateRangeOption?: DateRangeOption;
}

const TaxSavingsCardView: React.FC<TaxSavingsCardViewProps> = ({ dateRangeOption = DateRangeOption.AllTime }) => {
  const { data } = useGraphQLQuery(TaxSavingsDocument, {
    variables: { dateRangeOption },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;
  if (!data.performance.totals.investing?.allTimeTlhEstimatedTaxSavings) return null;
  if (!data.performance.totals.investing?.taxLossesHarvested) return null;

  const allTimeTaxSavingsDisplayValue = formatMoney(data.performance.totals.investing.allTimeTlhEstimatedTaxSavings);

  const taxLossesHarvestedDisplayValue = formatMoney(data.performance.totals.investing.taxLossesHarvested);

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300' }}>
        <Heading as="h2" variant="title200">
          Tax savings
        </Heading>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300', display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
          <Text>Estimated taxes saved</Text>
          <Text variant="text400" css={{ color: 'teal' }}>
            {allTimeTaxSavingsDisplayValue}
          </Text>
        </Box>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>
            Tax losses harvested
            <TaxLossHarvestingTooltip isEnabled={true} />
          </Text>
          <Text>{taxLossesHarvestedDisplayValue}</Text>
        </Box>
      </Card.Content>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Footer css={{ padding: 'space.300' }}>
        <TaxLossHarvestingPerformanceModal />
      </Card.Footer>
    </Card>
  );
};

export const TaxSavingsCard: React.FC<TaxSavingsCardViewProps> = props => (
  <ErrorBoundary fallbackType="none">
    <TaxSavingsCardView {...props} />
  </ErrorBoundary>
);
